import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import {
  getUnicornComparisonByUnicornRegion,
  getUnicornOriginBreakdownByCountry,
} from "@/services/api/UnicornTracker";
import { Info } from "@phosphor-icons/react";
import CustomTooltip from "@/components/CustomTooltip";
import Image from "next/image";
import chartIcon from "@/assets/icons/chart-icon.webp";

const COUNTRY_ORIGIN_SEGMENTS = [
  {
    key: "homegrown",
    label: "Homegrown",
  },
  {
    key: "transplant_in",
    label: "Transplant-In",
  },
  {
    key: "transplant_out",
    label: "Transplant-Out",
  },
];

function normalizeCountryOriginBreakdown(payload) {
  const data = payload?.data || payload || {};
  const breakdown = data.breakdown || data;

  if (Array.isArray(breakdown)) {
    return COUNTRY_ORIGIN_SEGMENTS.map((segment) => {
      const item = breakdown.find((entry) => entry.key === segment.key);

      return {
        name: item?.label || segment.label,
        y: Number(item?.count ?? item?.unicorn_count ?? item?.y ?? 0),
      };
    });
  }

  return COUNTRY_ORIGIN_SEGMENTS.map((segment) => ({
    name: segment.label,
    y: Number(breakdown[`${segment.key}_count`] || breakdown[segment.key] || 0),
  }));
}

export default function ShareOfRegion({ selectedCountryName }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const searchParams = useSearchParams();
  const selectedUnicornRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const unicornStatus = searchParams.get("unicorn_status");
  const originFilter = searchParams.get("origin_filter");

  useEffect(() => {
    async function fetchCountryOriginData(params) {
      const countryParams = { ...params };
      delete countryParams.metric;

      const response = await getUnicornOriginBreakdownByCountry(
        selectedCountryId,
        { params: countryParams },
      );

      return normalizeCountryOriginBreakdown(response.data?.data);
    }

    async function fetchShareData() {
      try {
        setLoading(true);

        const params = { metric: "count" };

        if (selectedUnicornRegionId)
          params.unicorn_regions = selectedUnicornRegionId;
        if (selectedCountryId) params.countries = selectedCountryId;

        if (sectors) params.sectors = sectors;
        if (stages) params.stages = stages;

        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }

        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }

        if (timeToUnicornMin) params.min_time_to_unicorn = timeToUnicornMin;
        if (timeToUnicornMax) params.max_time_to_unicorn = timeToUnicornMax;
        if (originFilter) params.origin_filter = originFilter;
        const statuses = unicornStatus
          ? unicornStatus.split(",").filter(Boolean)
          : ["current", "fallen", "alumni"];
        params.unicorn_status = statuses.join(",");
        if (statuses.includes("fallen")) params.include_lost_unicorn = 1;
        if (statuses.includes("alumni")) params.include_alumni = 1;
        if (!statuses.includes("current")) params.exclude_current_unicorn = 1;

        if (selectedCountryId) {
          setChartData(await fetchCountryOriginData(params));
          return;
        }

        const response = await getUnicornComparisonByUnicornRegion({
          params: {
            ...params,
            include_countries: true,
            country_limit: 50, // fetch enough to build Top 5 + Other reliably
          },
        });
        const comparison = response.data?.data?.comparison || [];

        // - If a unicorn region is selected, comparison[0].top_countries contains country counts for that region.
        // - Else, it was showing regions.

        let rawCountries = [];

        if (selectedUnicornRegionId && comparison.length > 0) {
          const regionData = comparison[0];
          rawCountries = (regionData.top_countries || []).map((c) => ({
            name: c.country_name,
            y: Number(c.unicorn_count || 0),
          }));
        } else if (comparison.length > 0) {
          const merged = new Map();
          comparison.forEach((region) => {
            (region.top_countries || []).forEach((c) => {
              const key = c.country_name || "Unknown";
              const prev = merged.get(key) || 0;
              merged.set(key, prev + Number(c.unicorn_count || 0));
            });
          });
          rawCountries = Array.from(merged.entries()).map(([name, y]) => ({
            name,
            y,
          }));
        }

        // Sort desc and build Top 5 + Other
        rawCountries = rawCountries
          .filter((x) => x && x.name && Number.isFinite(x.y))
          .sort((a, b) => b.y - a.y);

        const top5 = rawCountries.slice(0, 5);
        const rest = rawCountries.slice(5);
        const otherTotal = rest.reduce((sum, item) => sum + (item.y || 0), 0);

        const finalData =
          otherTotal > 0 ? [...top5, { name: "Others", y: otherTotal }] : top5;

        setChartData(finalData);
      } catch (error) {
        console.error("Error fetching share data:", error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    }

    fetchShareData();
  }, [
    selectedUnicornRegionId,
    selectedCountryId,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    unicornStatus,
    originFilter,
  ]);

  const chartColors = selectedCountryId
    ? ["#00C49F", "#7E57C2", "#FF7043"]
    : [
        "#00C49F",
        "#7E57C2",
        "#FFB300",
        "#29B6F6",
        "#FF7043",
        "#9E9E9E",
      ]; // last for "Others"

  const total = chartData.reduce((sum, item) => sum + (item.y || 0), 0);
  const isCountryBreakdown = Boolean(selectedCountryId);

  const options = {
    chart: {
      backgroundColor: "transparent",
      plotBackgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
      type: "pie",
    },
    title: { text: null },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const count = this.point.y || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return `<b>${this.point.name}</b><br/>${Highcharts.numberFormat(
          count,
          0,
        )} unicorns • ${Highcharts.numberFormat(pct, 1)}%`;
      },
    },
    plotOptions: {
      pie: {
        innerSize: "80%",
        borderColor: "#161617",
        borderWidth: 4,
        dataLabels: { enabled: false },
        showInLegend: false,
        cursor: "pointer",
        states: {
          hover: {
            enabled: true,
            halo: { size: 6 },
          },
        },
      },
    },
    legend: { enabled: false },
    series: [
      {
        name: isCountryBreakdown
          ? "Country origin breakdown"
          : "Unicorn births",
        colorByPoint: true,
        colors: chartColors,
        data: chartData,
      },
    ],
    credits: { enabled: false },
  };

  const centerTitle = isCountryBreakdown
    ? "Homegrown vs. Transplanted"
    : "Unicorn Births by Founding Country";
  const tooltipContent = isCountryBreakdown
    ? `Breakdown of unicorns connected to ${selectedCountryName || "this country"} by origin: homegrown, transplant-in, and transplant-out.`
    : "Counts when a company first reached a $1B+ valuation, grouped by founding HQ: where the company started, not where it is today.";

  // Empty state: no donut
  const showEmpty = !loading && total === 0;

  return (
    <div>
      <div className='relative z-10 flex items-center justify-between gap-1.5 mb-2'>
        <p className='text-base font-medium sm:text-sm'>{centerTitle}</p>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content={tooltipContent}
        />
      </div>
      <div className='relative'>
        {/* Center overlay */}
        <div className='absolute flex flex-col items-center -mt-2 text-center -translate-x-1/2 -translate-y-1/2 pointer-events-none top-1/2 left-1/2'>
          <Image src={chartIcon.src} layout='fill' alt='Total Funding' />
          <svg
            className='size-14'
            viewBox='0 0 48 48'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              opacity='0.2'
              d='M18.0002 7.03125V20.5312L6.30025 27.2812C5.51922 23.0656 6.26859 18.7096 8.41349 14.9972C10.5584 11.2849 13.9579 8.46011 18.0002 7.03125Z'
              fill='url(#paint0_linear_9050_41543)'
            />
            <path
              d='M18.7503 21.8306C18.9784 21.6989 19.1677 21.5095 19.2994 21.2814C19.4311 21.0533 19.5003 20.7946 19.5003 20.5312V7.03125C19.4989 6.7921 19.4404 6.55674 19.3297 6.3448C19.2189 6.13285 19.059 5.95047 18.8634 5.81286C18.6678 5.67524 18.4422 5.58639 18.2053 5.55372C17.9684 5.52104 17.7271 5.54548 17.5015 5.625C13.1234 7.17453 9.4415 10.235 7.11781 14.2561C4.79412 18.2773 3.98101 22.9955 4.82465 27.5625C4.86838 27.7987 4.96818 28.0209 5.11566 28.2105C5.26314 28.4 5.45399 28.5514 5.67215 28.6519C5.86875 28.7438 6.08324 28.7912 6.30027 28.7906C6.56355 28.7907 6.82221 28.7215 7.05027 28.59L18.7503 21.8306ZM16.5003 9.30375V19.665L7.52277 24.8456C7.50027 24.5625 7.50027 24.2775 7.50027 24C7.50294 20.9594 8.34457 17.9786 9.93251 15.3856C11.5205 12.7926 13.7931 10.6881 16.5003 9.30375ZM24.0003 4.5C23.6025 4.5 23.2209 4.65804 22.9396 4.93934C22.6583 5.22064 22.5003 5.60218 22.5003 6V23.2162L7.7234 31.8244C7.55192 31.9238 7.40185 32.0562 7.28185 32.2139C7.16185 32.3717 7.0743 32.5516 7.02426 32.7434C6.97422 32.9352 6.96269 33.135 6.99032 33.3313C7.01796 33.5275 7.08422 33.7164 7.18527 33.8869C8.91722 36.8312 11.3922 39.2689 14.3625 40.9559C17.3328 42.643 20.6941 43.5201 24.11 43.4996C27.5259 43.4792 30.8764 42.5617 33.8263 40.8391C36.7761 39.1166 39.2217 36.6494 40.9182 33.6845C42.6147 30.7196 43.5025 27.3611 43.4929 23.9451C43.4833 20.5292 42.5765 17.1757 40.8634 14.2204C39.1502 11.2651 36.6908 8.81172 33.7314 7.10579C30.7719 5.39986 27.4162 4.50127 24.0003 4.5ZM24.0003 40.5C21.3817 40.493 18.8021 39.8662 16.4723 38.6708C14.1425 37.4754 12.1288 35.7455 10.5959 33.6225L24.7559 25.3725C24.9837 25.241 25.1729 25.0519 25.3046 24.8241C25.4362 24.5964 25.5056 24.338 25.5059 24.075V7.5675C29.7367 7.95135 33.6564 9.95221 36.449 13.1535C39.2416 16.3548 40.6918 20.5099 40.4978 24.7536C40.3038 28.9973 38.4805 33.0027 35.4074 35.9358C32.3344 38.8689 28.2484 40.5038 24.0003 40.5Z'
              fill='url(#paint1_linear_9050_41543)'
            />
            <defs>
              <linearGradient
                id='paint0_linear_9050_41543'
                x1='6.73854'
                y1='23.9677'
                x2='18.0538'
                y2='23.8775'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#FFD338' />
                <stop offset='1' stopColor='#CD4F4F' />
              </linearGradient>
              <linearGradient
                id='paint1_linear_9050_41543'
                x1='6.90274'
                y1='37.1183'
                x2='43.6627'
                y2='36.6242'
                gradientUnits='userSpaceOnUse'
              >
                <stop stopColor='#FFD338' />
                <stop offset='1' stopColor='#CD4F4F' />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className='-my-10'>
          {loading ? (
            <div className='h-[320px] rounded-xl bg-white/5 animate-pulse' />
          ) : showEmpty ? (
            <div className='h-[320px] flex items-center justify-center text-center px-6'>
              <p className='text-sm font-semibold'>
                {isCountryBreakdown
                  ? "No origin breakdown recorded for this selection."
                  : "No unicorn births recorded for this selection."}
              </p>
            </div>
          ) : (
            <HighchartsReact highcharts={Highcharts} options={options} />
          )}
        </div>
      </div>

      {!loading && !showEmpty && (
        <div className='flex flex-wrap justify-center pb-6 gap-x-3 gap-y-2'>
          {chartData.map((item, i) => (
            <div
              key={item.name}
              className='inline-flex items-center gap-1.5 text-[10px]'
            >
              <span
                className='rounded-full size-2'
                style={{ backgroundColor: chartColors[i] }}
              />
              <span className='opacity-60'>{item.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
